import clsx from 'clsx';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import { useChanges, useProject, useTasks, useThreads } from '@/api';
import type { Project } from '@/api/types';
import { Status } from '@/components/ui';
import { QueryPending } from '@/components/QueryState';
import { health } from '@/lib/status';

export type ProjectContext = { project: Project };

export function ProjectShell() {
  const { projectId = '' } = useParams();
  const projectQ = useProject(projectId);
  const project = projectQ.data;
  const { data: tasks } = useTasks(projectId);
  const { data: threads } = useThreads(projectId);
  const { data: changes } = useChanges(projectId);

  if (!project) return <QueryPending query={projectQ} />;

  const open = tasks?.filter((t) => t.status !== 'done').length;
  const unread = threads?.reduce((n, t) => n + t.unread, 0);
  const h = health[project.health];
  const tabs = [
    { to: '', label: 'Сейчас', end: true },
    { to: 'tasks', label: 'Задачи', count: open, tour: 'tab-tasks' },
    { to: 'talk', label: 'Обсуждения', count: unread, strong: true },
    { to: 'reports', label: 'Отчёты', tour: 'tab-reports' },
    { to: 'meetings', label: 'Встречи' },
    { to: 'changes', label: 'Идеи и запросы', count: changes?.filter((c) => c.status === 'estimated' || c.status === 'question').length, strong: true },
    { to: 'files', label: 'Файлы' },
    { to: 'terms', label: 'Условия' },
  ];

  return (
    <>
      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <h1 className="text-xl font-semibold tracking-tight">{project.title}</h1>
          <Status tone={h.tone}>{h.label}</Status>
        </div>
        <p className="mt-1 max-w-3xl text-muted">{project.summary}</p>
      </header>
      <nav className="mb-8 flex gap-6 overflow-x-auto border-b border-line" aria-label="Разделы проекта">
        {tabs.map((t) => (
          <NavLink
            key={t.label}
            to={t.to}
            end={t.end}
            data-tour={t.tour}
            className={({ isActive }) =>
              clsx('-mb-px shrink-0 border-b-2 pb-2.5 text-sm transition-colors', isActive ? 'border-ink font-medium text-ink' : 'border-transparent text-muted hover:text-ink')
            }
          >
            {t.label}
            {!!t.count && (
              <span className={clsx('num ml-1.5', t.strong ? 'rounded-full bg-ink px-1.5 text-[10px] font-semibold leading-4 text-inverse' : 'text-faint')}>{t.count}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <Outlet context={{ project } satisfies ProjectContext} />
    </>
  );
}
